// @ts-strict-ignore
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import {
  Location
} from '@angular/common';

import { SohoBusyIndicatorDirective, SohoTabsComponent, } from 'ids-enterprise-ng';
import { LandmarkPanel, LMFormContentBase } from './lm-form.model';
import { LMFormPanelBase } from './lm-form-panel-base';
import { LMFormPanelBaseService, LMPanelTransactionEvent } from '@form/services/lm-form-panel.service';
import { LMModel, LMModelChangedEvent } from './model/lm-model';
import { LMFormState } from '@states/models/lm-form-state.model';
import { LMFormPersonalization } from '@states/models/lm-form-personalization.model';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMWorker } from '../../types/lm-worker';
import { LMInvalidFieldEvent } from '@field/lm-field-base.component';
import { LMContextService, LMLoggerService, LMOverlayService, LMUrlUtilService } from '@services/index';
import { LMFormPanelFormComponent } from './lm-form-panel-form.component';
import { LMDashboardViewComponent } from '@dashboard/lm-dashboard-view.component';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { AppConstants, LMConditionUtilService } from '@utils/index';
import { takeUntil } from 'rxjs/operators';
import { LMFormPanelMultiListComponent } from './lm-form-panel-multilist.component';
import { LMColorId, LMThemeService } from '@banner/lm-theme.service';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { LMModelField } from './model';
import { LMFormPanelUrlComponent } from './lm-form-panel-url.component';
import { LMFormPanelDashboardComponent } from './lm-form-panel-dashboard.component';
import { LMListTransactionEvent } from '@list/lm-list.component';
import { LMFormService, LMFormSpecUtilService } from './services';
import { LMGlobalPersonalizationService } from '@states/services';
import { LMActionFormDialogResultCode } from '@modal/index';
import { LMFormPanelListComponent, LMListSpecUtilService, LMRefreshRequest, LMReportFieldPickerService } from '@app/components';
import { SubscriptionLike as ISubscription } from 'rxjs/internal/types';

export class LMPanelSaveWorker extends LMWorker {
  private readonly formPanels: LMFormPanelBase[];

  constructor(formPanels: LMFormPanelBase[]) {
    super();

    this.formPanels = formPanels;
  }

  protected has(i: number): boolean {
    return (i < this.formPanels.length);
  }

  protected get(i: number): LMFormPanelBase {
    return this.formPanels[ i ];
  }

  protected run(i: number): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const formPanel = this.formPanels[ i ];
      // Panel component does not check for dirty. Instead composite or stepprocess component will decide how to run save action.
      // Step process rules to run save before next will be determined lm-form-pane.service getPanelTransaction
      // Composite form save worker will run save if panel is dirty.
      // Dashboard view also has similar save worker checking for dirty state before save.
      if (formPanel?.isDirty()) {
        formPanel.save(observer);
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }
}

@Component({
  selector:    'lm-form-content-composite',
  standalone: false,
  providers:   [ LMFormPanelBaseService ],
  templateUrl: 'lm-form-content-composite.component.html',
  styleUrls:   [ 'lm-form-content-composite.component.scss' ]
  })
export class LMFormContentCompositeComponent implements AfterViewInit, AfterViewChecked, OnInit, OnDestroy, LMFormContentBase {

  @HostBinding('class.on-side') showContextFormOnSide = true;
  @HostBinding('class.implements-cart-view') get implementsCart() { return this.formSpec.busFormSpec.implementsCartView; }
  @HostBinding('class.lm-is-compact-form-layout') isCompactFormLayout: boolean;
  // selected panel has a dashboard
  @HostBinding('class.implements-dash-board-view') get implementsDashBoardView(): boolean {
    if (this.panels.length > 0 && this.selectedPanelIndex !== undefined) {
      const panel = this.panels[this.selectedPanelIndex];
      if (panel?.type === 'dashboard') {
        return true;
      }
      if (panel?.type === 'formlist') {
        const busListSpec = panel?.listSpec?.busListSpec;
        return !!busListSpec?.implementsDashBoardView ||
            busListSpec?.detailSections?.[0]?.type === 'dashboard';
      }
    }
    return false;
  }
  @HostBinding('class.lm-vertical-tabs') verticalTabs: boolean;
  @HostBinding('class.lm-parent-is-modal') @Input() parentIsModal: boolean;

  // homepage-background is an IDS class used to add gray background to the parent container
  @HostBinding('class.homepage-background') get homepageBackground() {
    return this?.formSpec?.busFormSpec?.implementsDashBoardView;
  }

  // Need to know if the window has shrunk, in order to correctly set the tab container height
  @HostListener('window:resize') public onResize() {
    this.doRecalculateHeight = true;
  }

  // @HostListener('click', [ '$event' ]) onClick(e: PointerEvent) {
  //   if (this.isInOverlay) {
  //     e.stopPropagation();
  //     e.stopImmediatePropagation();
  //   }
  // }

  @Input() formOptions: LMFormOptions;
  @Input() specFields: LMSpecFields;
  @Input() formUrlInfo: LMFormUrlInfo = null;
  @Input() formSpec: LMFormSpec = null;
  @Input() model: LMModel;
  @Input() formState: LMFormState;
  @Input() placeHolder: ViewContainerRef;
  @Input() formPersonalization: LMFormPersonalization;
  @Input() isInOverlay = false;

  @ViewChildren('compositeComponent') compositeComponents: QueryList<LMFormPanelBase>;
  @ViewChildren('flexContent') flexContent: QueryList<ElementRef>;
  @ViewChild('contextContext') contextContent: ElementRef;
  @ViewChild('dashboardViewComponent') dashboardViewComponent: LMDashboardViewComponent;
  @ViewChild('tabPanelContainer', { read: ElementRef }) tabContainerElement: ElementRef;
  @ViewChild('sohoTabsComponent') sohoTabsComponent: SohoTabsComponent;
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) sohoBusyIndicator: SohoBusyIndicatorDirective;

  @Output() contentModified = new EventEmitter<any>();
  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();
  @Output() dashboardContentDisplayed = new EventEmitter<LMContentDisplayedEvent>();
  @Output() updateToolbarStateEvent = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() sendShortcutMessage = new EventEmitter<any>();
  @Output() specialActionsToolbarButtonEvent = new EventEmitter<LMSpecialActionToolbarButtonEvent>();
  @Output() panelTransactionEvent = new EventEmitter<LMPanelTransactionEvent>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundEvent>();
  
  public panels: Array<LandmarkPanel>;
  public fromDataView: LMDataView;
  public contextForm: LMContextForm;
  public useThemeColor: boolean;
  public colorIsDefault: boolean;
  public useContextFormBackgroundImage: boolean;
  public personalize: boolean;
  public contextFormBackgroundImageUrl: SafeStyle;
  public contextFormColorClass: string;
  public lightContextForm = false;
  public formPanelUrlDisplayed = false;
  public activatedTabChanged: boolean;
  public useLazyLoadForm = false;
  public onSubForm = false;

  public usedInCartView = false;

  private lazyDashboardConditions: boolean;
  private updateSinglePanelLazyDashboardConditionFields: boolean;
  private updateMultiPanelLazyDashboardConditionFields: boolean;

  private _hasPendingSelection = false;
  private _pendingSelectedPanel: LandmarkPanel;
  private m_componentDestroyed = false;
  private subscriptionManager: Subject<void> =  new Subject<void>();
  private instanceId: string;

  private runHandleResizeTabsComponent = false;
  private initializeSohoTabs = false;
  private selectedPanelIndex: number;
  public panelVisibilityUpdateTimeStamp: number;

  private _singlePanel: LandmarkPanel;
  private _hasTabs: boolean;

  private doRecalculateHeight = true;
  public tabPanelContainerHeight = 'auto';
  public hideBorder = false;
  public testVersion1: boolean;
  // public cartHeight: '700px;';

  public tabsetAutomationId: string;
  public formPanelTabAttributes: SohoAttributesArray = [];

  public readonly tabIdSuffix = AppConstants.ELEMENT_ID_SUFFIX_FORM_PANEL_TAB;
  public uiLoadingIndicatorTimeout = 0;

  public use100HeightContainer = false;

  public useFullViewPointBackground = false;
  public fullViewPointBackgroundImageUrl: SafeStyle;
  public foregroundLight = false;
  public foregroundDark = false;
  public fullPageBackgroundNotTwelveColumn = false;

  private subscriptions: ISubscription[] = [];
  public hasBreadcrumbs = false;
  private isCreateReportMode = false;

  public contextFormClasses: string;
  public useVerticalTabs = false;
  private observer: ResizeObserver;
  private formOverlayElement: Element;

  public usePersonalizableTabs = false;
  public useAlternateTabs = false;
  public isHideTitleBar = false;

  private overlayClickUnlistenEvent: () => void;

  private widthObserver: ResizeObserver;

  constructor(
    private location: Location,
    private ngZone: NgZone,
    private _sanitizer: DomSanitizer,
    private renderer2: Renderer2,
    private element: ElementRef,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmFormPanelBaseService: LMFormPanelBaseService,
    private lmLoggerService: LMLoggerService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmThemeService: LMThemeService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmContextService: LMContextService,
    private lmFormService: LMFormService,
    private lmFormSpecUtilService: LMFormSpecUtilService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmListSpecUtilService: LMListSpecUtilService,
    private lmReportFieldPickerService: LMReportFieldPickerService,
    private lmOverlayService: LMOverlayService
  ) {
    this.instanceId = this.lmLoggerService.instanceId;
    this.useLazyLoadForm = this.lmContextService.useLazyLoadForm();
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
    this.isHideTitleBar = this.lmContextService.isHideTitlebar() || this.lmContextService.inMingleInbox();
  }

  ngOnInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngOnInit()' ]);

    this.hasBreadcrumbs = this.lmContextService.isBreadCrumbsDisplayed();
    this.usedInCartView = this.formSpec.busFormSpec.implementsCartView;

    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;

    this.tabsetAutomationId = `lm-${this.formSpec.formElementId}-tabs`;
    this.formPanelTabAttributes.push({ name: 'data-automation-id', value: 'lm-' + this.formSpec.formElementId + '-tabs' });

    if (!this.testVersion1) {
      this.formPanelTabAttributes.push({ name: 'id', value: this.formSpec.formElementId + '_tab' });
    }

    this.lmFormPanelBaseService.buildCompositePanels(this.formSpec.busFormSpec, this.model, this.formSpec, true, this.isInOverlay);
    this.panels = this.lmFormPanelBaseService.getPanels();
    this.isCompactFormLayout = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;

    if (this.formSpec.busFormSpec.background) {
      this.useFullViewPointBackground = this.formSpec.busFormSpec.background.useBackgroundImage;
    }

    if (this.formSpec.busFormSpec.contextForm && this.formSpec.busFormSpec.implementsDashBoardView) {
      this.showContextFormOnSide = !this.formSpec.busFormSpec.showContextFormOnTop;
    } else if (this.formSpec.busFormSpec.contextForm && !this.formSpec.busFormSpec.implementsDashBoardView) {
      this.showContextFormOnSide = !!this.formSpec.busFormSpec.showContextFormOnLeft;
    } else {
      this.showContextFormOnSide = false;
    }

    // Force tabs to display horizontal if context form is on the side.
    this.verticalTabs = this.lmFormPanelBaseService.getVerticalTabs() && !this.showContextFormOnSide;
    this.contextForm = this.lmFormPanelBaseService.getContextForm();
    this.onSubForm = this.lmFormSpecUtilService.isSubForm(this.formSpec);

    this.setSelectedIndexFromParam();
    // if (!this.implementsCart) {
    //   this.selectedPanelIndex = this.lmFormPanelBaseService.getPanelIndexToSelect(this.formUrlInfo, this.formState);
    // }

    // don't apply color styling if in the flyout/overlay
    if (!this.verticalTabs && !this.showContextFormOnSide && !this.isInOverlay) {
      // ---------------------------------------------------------------
      // only apply context form styling if tabs are NOT vertical.
      // ---------------------------------------------------------------
      if (!this.contextForm) {
        // ---------------------------------------------------------------
        // if no context form then use personalize color for tabs.
        // ---------------------------------------------------------------
        this.useThemeColor = true;
        this.colorIsDefault = this.lmThemeService.getCurrentColorId() === LMColorId.alabaster;

      } else {
        this.personalize = this.lmThemeService.isPersonalized(true, this.formSpec.busFormSpec, this.parentIsModal);

        if (this.personalize && this.contextForm.background) {
          if (this.contextForm.background.useThemeColor) {
            this.useThemeColor = true;

            // theme is only set when navigating after load, so need to subscribe to the theme and color changes.
            // only have lighter theme context forms if in the light themes
            if(this.lmThemeService.getCurrentThemeId().includes('light')) {
              this.colorIsDefault = this.personalize = false;
              this.lightContextForm = true;
              this.contextFormColorClass = this.lmThemeService.getContextFormBackgroundColorClass();
            }

            // scope to only the light themes for now
            const themeSubject = this.lmThemeService.getThemeChangeSubject().subscribe((theme: string) => {
              if (theme?.includes('light')) {
                this.lightContextForm = true;
                this.colorIsDefault = false;
                this.contextFormColorClass = this.lmThemeService.getContextFormBackgroundColorClass();
              } else {
                this.personalize = true;
                this.lightContextForm = false;
                this.colorIsDefault = this.lmThemeService.getCurrentColorId() === LMColorId.alabaster;
                this.contextFormColorClass = '';
              }
            });

            const colorChange = this.lmThemeService.getColorChangeSubject().subscribe((color: string) => {
              this.contextFormColorClass = this.lmThemeService.getContextFormBackgroundColorClass(color);
            });

            this.subscriptions.push(themeSubject);
            this.subscriptions.push(colorChange);


            // // todo should have been handled automatically for "default" theme color. Ping tim
            // this.lmThemeService.getColorChangeSubject().subscribe(colorId => {
            //   // default color means no color is used for menu, context forms, and composite tabs.
            //   this.colorIsDefault = colorId === LMColorId.default
            // });

          } else if (this.contextForm.background.useBackgroundImage) {
            this.useContextFormBackgroundImage = true;
          }
        }
      }
    }

    this.panels.forEach((panel2: LandmarkPanel) => {
      if (panel2.type === 'dashboard') {
        panel2.dashBoardPanes = this.lmFormPanelBaseService.createSubPanels(panel2.busFormPanel.panels, this.formSpec.busFormSpec, this.model, this.formSpec);
        if (panel2.busFormPanel.dashboardColumns) {
          panel2.dashboardColumns = panel2.busFormPanel.dashboardColumns;
        }
      }
    });

    this._calculateHasTabs();
    this._setSinglePanel();
    this._setBackgroundImageUrl();
    this.isPersonalizable();
    this.alternateTabs();
    this.buildContextFormClasses();

    // lazyDashboardConditions will not be implemented for this.formSpec.busFormSpec.implementsDashBoardView
    this.lazyDashboardConditions = this.lmContextService.lazyConditionsOnNestedDashBoardCompositeFormPanels() && !this.formSpec.busFormSpec.implementsDashBoardView; // this._singlePanel !== null;

    // meaning this is a single tab panel that is a dashboard view.
    if (this.lazyDashboardConditions && !this.hasTabs && this._singlePanel?.type === 'dashboard') {
      this.updateSinglePanelLazyDashboardConditionFields = true;
    }

    this.model.modelChanged.pipe(takeUntil(this.subscriptionManager)).subscribe((modelChangedEvent: LMModelChangedEvent) => {
      let sourceIsPanel = false;
      let originatingPanel: string = null;
      let originatingList: string = null;

      if (modelChangedEvent.source) {
        if (modelChangedEvent.source instanceof LMPanelTransactionEvent) {
          sourceIsPanel = true;
          originatingPanel = modelChangedEvent.source.originatingPanel;
          originatingList = modelChangedEvent.source.originatingList;
        } else if (modelChangedEvent.source instanceof LMListTransactionEvent) {
          sourceIsPanel = true;
          originatingList = modelChangedEvent.source.eventSource ? modelChangedEvent.source.eventSource.listId : null;
        }
        if (modelChangedEvent.source.type === 'transaction') {
          //  When breadcrumbs are displayed, the sticky tabs will stop at a different height
          this.hasBreadcrumbs = this.lmContextService.isBreadCrumbsDisplayed();
        }
      }

      this.updateVisibleWhenTabs();

      if (modelChangedEvent.eventType !== 'update') {
        this.updateFromDataView(sourceIsPanel, originatingList, originatingPanel);
      }

      if (modelChangedEvent.eventType === 'set') {
        this._setBackgroundImageUrl();

        if (this.lazyDashboardConditions) {
          if (!this.hasTabs) {
            // needed for when the dataView changes due to next/prev on a form that has a single
            // dashboard.
            if (this._singlePanel?.type === 'dashboard') {
              this.updateSinglePanelLazyDashboardConditionFields = true;
            }
          } else {
            this.updateMultiPanelLazyDashboardConditionFields = true;
          }
        }
      }
    });

    this.lmConditionUtilService.evaluateConditions.pipe(takeUntil(this.subscriptionManager)).subscribe(() => {
      this.updateVisibleWhenTabs();
    });

    this.initializeSohoTabs = true;

    const visibleWhenEventSubscription = this.lmFormService.visibleWhenEvent$
      .subscribe((elementRef: ElementRef) => this.onVisibleWhenEvent(elementRef));
    this.subscriptions.push(visibleWhenEventSubscription);

    const panel = this.panels[ this.selectedPanelIndex ];
    this.setTabContainerHeight(panel);

    this.lmReportFieldPickerService.isCreateReportMode$
      .pipe(takeUntil(this.subscriptionManager))
      .subscribe((state) => {
        this.isCreateReportMode = state;
        this.updateVisibleWhenTabs();
    });

    this.useVerticalTabs = this.verticalTabs;

    // display the buttons under the toolbar title for mingle inbox
    if (this.lmContextService.useListFormOverlay() && this.isInOverlay) {
      if (this.verticalTabs) {
        this.observer = new ResizeObserver(entries => {
          entries.forEach(entry => {
            if (entry.contentRect.width > 600) {
              this.useVerticalTabs = true;
              setTimeout(() => {
                const nearestPanel = this.findNearestVisibleValidPanel(this.selectedPanelIndex);
                if (nearestPanel) {
                  if (this.sohoTabsComponent) {
                    this.sohoTabsComponent.select('#' + nearestPanel.id, true);
                  }
                }
              }, 1);
            } else {
              this.useVerticalTabs = false;
              setTimeout(() => {
                const nearestPanel = this.findNearestVisibleValidPanel(this.selectedPanelIndex);
                if (nearestPanel) {
                  if (this.sohoTabsComponent) {
                    this.sohoTabsComponent.select('#' + nearestPanel.id, true);
                  }
                }
              }, 1);
            }
          });
        });

        this.formOverlayElement = document.querySelector('lm-sidebar-resize');
        this.observer.observe(this.formOverlayElement);
      } else if (this.showContextFormOnSide) {
        // show vertical tabs and context form on side are mutually exclusive, so need to set up the observer for
        // moving context form on side
        this.observer = new ResizeObserver(entries => {
          entries.forEach(entry => {
            if (entry.contentRect.width > 680) {
              this.moveContextForm(true);
            } else {
              this.moveContextForm(false);
            }
          });
        });

        this.formOverlayElement = document.querySelector('lm-sidebar-resize');
        this.observer.observe(this.formOverlayElement);
      }

      if (this.element) {
        this.overlayClickUnlistenEvent = this.renderer2.listen(this.element.nativeElement, 'click', (event: PointerEvent) => {
          // making sure click in the overlay doesn't cause the overlay to close. Move this to a HostListener when
          // overlay code is not controlled by a CSK anymore
          if (this.isInOverlay) {
            event.stopPropagation();
            event.stopImmediatePropagation();
          }
        });
      }
    }

    // need to set up a resize observer for the case of a single panel that is
    // a dashboard, so we can call the refresh element on the dashboard panel
    // when the app menu is opened/closed. This is done in IDS for when there
    // is a tab component (multi-panel), but we need to do it for single
    if (this.showContextFormOnSide && (this.formSpec.busFormSpec.implementsDashBoardView || (this.singlePanel && this.singlePanel.type === 'dashboard'))) {
      this.widthObserver = new ResizeObserver(() => {
        this.panelVisibilityUpdateTimeStamp = Date.now();
      });

      this.widthObserver.observe(this.element.nativeElement);
    }
  }

  ngAfterViewInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngAfterViewInit()' ]);
    this.runHandleResizeTabsComponent = true;

    // set fromDataView from models dataview so if flows down to the
    // related composite form panels (multi-list, list, etc..).
    setTimeout(() => this.fromDataView = this.model.getData());
  }

  ngAfterViewChecked() {
    if (this.runHandleResizeTabsComponent) {
      this.runHandleResizeTabsComponent = false;
      if (this.hasTabs && this.sohoTabsComponent) {
        this.sohoTabsComponent.handleResize();
      }
    }

    // needed for when the dataView changes due to next/prev on a form that has a single
    // dashboard tab due to either LPL having one tab or changing due to conditions
    // for the tabs.
    if (this.lazyDashboardConditions) {
      if (this.updateSinglePanelLazyDashboardConditionFields) {
        this.updateSinglePanelLazyDashboardConditionFields = false;
        if (this._singlePanel?.type === 'dashboard') {
          setTimeout(() => {
            // didn't work w/o the setTimeout
            this.lmFormPanelBaseService.updateDashboardPanelVisibility(this._singlePanel.dashBoardPanes, this.model).subscribe(result => {
              if (result === true) {
                this.panelVisibilityUpdateTimeStamp = this.lmFormPanelBaseService.getPanelDisplayStateLastChanged();
              }
            });
          });
        }
      }

      if (this.updateMultiPanelLazyDashboardConditionFields) {
        this.updateMultiPanelLazyDashboardConditionFields = false;
        // setTimeout(() => {       // I don't think  this setTimeout is needed.
          const formPanel = this.panels[ this.activeTabIndex ];
          if (formPanel?.type === 'dashboard') {
            this.lmFormPanelBaseService.updateDashboardPanelVisibility(formPanel.dashBoardPanes, this.model).subscribe(result => {
              if (result === true) {
                this.panelVisibilityUpdateTimeStamp = this.lmFormPanelBaseService.getPanelDisplayStateLastChanged();
              }
            });
          }
        // });
      }
    }

    if (this.initializeSohoTabs) {
      this.initializeSohoTabs = false;
      if (this.hasTabs && this.sohoTabsComponent) {
        this.panelVisibilityUpdateTimeStamp = this.lmFormPanelBaseService.getPanelDisplayStateLastChanged();
        if (this.selectedPanelIndex > -1) {
          const nearestPanel = this.findNearestVisibleValidPanel(this.selectedPanelIndex);
          // causes a 2nd call to activateTab on initial load.
          // make sure it actually needs to change before calling
          // select on the nearestPanel. It should never need to call this
          // on an initial load.
          if (nearestPanel && nearestPanel.index !== this.selectedPanelIndex) {  // no need to select a new tab if the nearestPanel.index === this.selectedPanelIndex
            if (this.sohoTabsComponent) {
              this.sohoTabsComponent.select('#' + nearestPanel.id, true);
            }
          }
        }
      }
    }

    if (this._hasPendingSelection) {
      setTimeout(() => this.handlePendingSelection());
      this._hasPendingSelection = false;
    }

    if (this.doRecalculateHeight) {
      this.doRecalculateHeight = false;
    }
  }

  ngOnDestroy() {
    this.setComponentDestroyed();

    this.subscriptionManager.next();
    this.subscriptionManager.complete();

    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];

    if (this.lmContextService.useListFormOverlay() && this.isInOverlay) {
      if (this.observer) {
        this.observer.unobserve(this.formOverlayElement);
      }
      if (this.overlayClickUnlistenEvent) {
        // unlisten to click event
        this.overlayClickUnlistenEvent();
      }
    }

    if (this.widthObserver) {
      this.widthObserver.unobserve(this.element.nativeElement);
    }
  }

  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onFieldEvent(event) {
    if ('FieldInvalidEvent' === event.eventType) {
      // switch to the tab with the field in error
      const fieldChangedEvent = event as LMInvalidFieldEvent;

      if (fieldChangedEvent.panelId) {
        // find the visible tab and select it
        if (this.panels.some( (panel: LandmarkPanel) => panel.id === fieldChangedEvent.panelId && !panel.hidden)) {
          this.sohoTabsComponent.select('#' + fieldChangedEvent.panelId);

        } else {
          // cannot find a panel, so display the error in a dialog
          this.lmErrorUtilService.handleUnexpectedError({ title: Soho.Locale.translate('Error'), message: event.alert.errorMessage });
        }
      }

      fieldChangedEvent.alert.scrollIntoView(false);
    }
    this.fieldEvent.emit(event);
  }

  onUpdateToolbarStateEvent() {
    this.updateToolbarStateEvent.emit();
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onSpecialActionToolbarButtonEvent(event: LMSpecialActionToolbarButtonEvent) {
    this.specialActionsToolbarButtonEvent.emit(event);
  }

  onPanelTransactionEvent(event: LMPanelTransactionEvent) {
    this.panelTransactionEvent.emit(event);
  }

  onDrillAroundEvent(event: LMDrillAroundEvent) {
    if (!this.singlePanel) {
      const selectedPanel = this.panels[ this.selectedPanelIndex ];

      if (selectedPanel) {
        const detail: LMDrillAroundDetail = {
          type: 'panel',
          panel: selectedPanel.title,
          panelIndex: this.selectedPanelIndex.toString(10),
          drillActions: undefined
        };
        event.chain.unshift(detail);
      }
    }

    this.drillAroundEvent.emit(event);
  }

  onTabActivated(event: SohoTabsEvent) {
    if (this.lmContextService.useListFormOverlay()) {

      if (this.isInOverlay) {
        // don't propogate the event up if in overlay, since that would close the overlay
        event.stopPropagation();
        event.stopImmediatePropagation();
      } else {
        // not in overlay, close it if the overlay is open
        if (this.lmOverlayService.hasOpenOverlay) {
          this.lmOverlayService.close();
        }
      }
    }
    /*
     * onTabActivated gets messaged before the sohoDataGrid has finished resolving it's template
     * so I need to put into a setTimeout allowing angular to have a chance to process the template
     * before I attempt to call methods on it.
     */
    setTimeout(() => {
      if (this.componentDestroyed) {
        this.lmLoggerService.log([ this, this.instanceId, 'onTabActivated() > timeout > component was destroyed before expected, returning.' ]);
        // safety in case the user has initiated a list but quickly navigated
        // to another screen and ngOnDestroy is in a race condition with this
        // timeout.
        return;
      }

      const selectedPanelIndex = this.activeTabIndex;
      const panel = this.panels[ selectedPanelIndex ];
      let showBusy = false;

      // same criteria as when activating a lazy load form tab
      if (panel && panel.type === 'form' && this.useLazyLoadForm && !panel.hidden && !panel.initialized) {
        // use the global busy indicator as it will run immediately
        this.showBusyIndicator();
        showBusy = true;
      }

      if (showBusy) {
        // if showBusy, then another timeout so the busy indicator can start
        setTimeout(() => {
          this.doActivateTab();
        }, 50);

      } else {
        this.doActivateTab();
      }
    }, 1);
  }

  public setSelectedIndexFromParam(ForceTabSelection?: boolean) {
      if (!this.implementsCart) {
          this.selectedPanelIndex = this.lmFormPanelBaseService.getPanelIndexToSelect(this.formUrlInfo, this.formState);

          if (ForceTabSelection) {

              const panel : LandmarkPanel = this.panels[ this.selectedPanelIndex ];
              this.selectSohoTab(panel.id);
              this.doActivateTab();
          }
      }
  }

  public get isAlabaster(): boolean {
    return this.lmThemeService.getCurrentColorId() === LMColorId.alabaster;
  }

  private doActivateTab() {
    this.activateTab();
    this.doRecalculateHeight = true;
    this.activatedTabChanged = !this.activatedTabChanged;
  }

  // Need to set the height on the tab container correctly. If the content of the panel is less than the
  // window height, need to set the containers height to match the view height of the window. If the content
  // of the tab is greater than the window height, then set the tab container's height to auto
  private handlePanelHeightChange() {
    // Set possible window height modifiers
    const contextFormOffset = (this.contextForm && !this.showContextFormOnSide) ? this.contextContent.nativeElement.offsetHeight : 0;

    const mingleOffset = this.lmContextService.inMingle() ? 34 : 0;

    const headerBarOffset = !this.lmContextService.isHideTitlebar() ? 60 : 0;

    const windowHeight = window.innerHeight - mingleOffset - headerBarOffset;

    if (this.hasTabs && this.verticalTabs) {

      // Set height to 100% if in responsive mode
      if (window.innerWidth < Soho.breakpoints['phone-to-tablet']) {
        this.tabPanelContainerHeight = '100%';
        // When going into responsive view, the border still appeared on the left side. Need to hide it.
        this.hideBorder = true;
        return;
      }

      // reset hiding border
      this.hideBorder = false;

      const selectedPanel = this.panels[ this.selectedPanelIndex ];

      // rule from the template
      if (this.formPanelUrlDisplayed) {
        this.tabPanelContainerHeight = '100%';
        return;
      }

      // Multi list panels are with over 2 lists always huge. Set to auto, so we don't have a timing issue waiting
      // for the lists to get populated with data
      if (selectedPanel.type === 'multilist') {
        let visibleLists = 0;
        this.compositeComponents.filter((basePanel: LMFormPanelBase) => basePanel.panelId === selectedPanel.id)
          .forEach((basePanel: LMFormPanelBase) => visibleLists = (basePanel as LMFormPanelMultiListComponent).numberVisibleLists);

        if (visibleLists > 2) {
          this.tabPanelContainerHeight = 'auto';
          return;
        }
      }

      const thePanel = this.compositeComponents.find((panel: LMFormPanelBase) => panel.panelId === selectedPanel.id);

      const panelHeight = thePanel ? thePanel.getPanelHeight() : 0;
      this.calculateContentPanelHeight(panelHeight, contextFormOffset, windowHeight);

    } else if (this.formSpec.busFormSpec.implementsDashBoardView) {
      const panelHeight = this.dashboardViewComponent ? this.dashboardViewComponent.getPanelHeight() : 0;
      this.calculateContentPanelHeight(panelHeight, contextFormOffset, windowHeight);
    } else if (this.formSpec.busFormSpec.implementsCartView) {
      const panelHeight = this.dashboardViewComponent ? this.dashboardViewComponent.getPanelHeight() : 0;
      this.calculateContentPanelHeight(panelHeight, contextFormOffset, windowHeight);
    } else {
      // TODO this single panel case may be able to included in the first if block.
      const selectedPanel = this.panels[ this.selectedPanelIndex ];
      const thePanel = this.compositeComponents.find((panel: LMFormPanelBase) => panel.panelId === selectedPanel.id);
      const panelHeight = thePanel ? thePanel.getPanelHeight() : 0;
      this.calculateContentPanelHeight(panelHeight, contextFormOffset, windowHeight);

    }
  }

  private calculateContentPanelHeight(panelHeight: number, contextFormOffset: number, windowHeight: number): void {
    let isNestedCardViewWithDetailsWithoutContext = false;
    const tabHeaderHeight = 40; //TODO: compute exact height

    // Determine if selected panel is a list panel with a card view with details sections in it, and the composite form
    // has a context form
    if (this.selectedPanelIndex > -1 && this.panels[this.selectedPanelIndex].type === 'formlist' && this.formSpec.busFormSpec.contextForm) {
      const listSpec = this.panels[this.selectedPanelIndex].listSpec;
      isNestedCardViewWithDetailsWithoutContext = this.lmListSpecUtilService.isCardViewWithDetailSections(listSpec);
    }

    if ((panelHeight + contextFormOffset) < windowHeight && !this.parentIsModal && !isNestedCardViewWithDetailsWithoutContext) {

      if (!this.contextForm || this.showContextFormOnSide) {
        this.tabPanelContainerHeight = this.hasTabs && !this.verticalTabs ? `calc(100% - ${tabHeaderHeight}px)` : '100%';
      } else {
        const heightOffset = tabHeaderHeight + contextFormOffset;
        this.tabPanelContainerHeight = 'calc(100% - ' + heightOffset + 'px)';
      }

    } else if (isNestedCardViewWithDetailsWithoutContext && !this.showContextFormOnSide) {
      // Context form NOT on left, formlist with detail section
      this.tabPanelContainerHeight = '100%';
    } else if(!this.contextForm || this.formSpec.busFormSpec.showContextFormOnLeft){
      this.tabPanelContainerHeight = this.hasTabs && !this.verticalTabs ? `calc(100% - ${tabHeaderHeight}px)` : '100%';
    } else {
      this.tabPanelContainerHeight = 'auto';
    }
  }

  private setTabContainerHeight(panel: LandmarkPanel): void {
    // set tab container height to 100% when the panel is a card view list with detail sections
    this.use100HeightContainer = panel?.type === 'formlist'
        && this.lmListSpecUtilService.isCardViewWithDetailSections(panel.listSpec)
        && !this.verticalTabs
        && !this.contextForm;

    // on mobile set tab container height to 100% for detail section lists that don't have a dashboard
    if (this.lmContextService.isMobileDevice()) {
      const busListSpec = panel?.listSpec?.busListSpec;
      const hasDetailSections = this.lmListSpecUtilService.hasDetailSections(panel?.listSpec);

      if (hasDetailSections) {
        let hasDetailSectionDashboard = false;
        if (busListSpec?.detailSections?.length > 0) {
          const firstDetailSection = busListSpec.detailSections[0];
          hasDetailSectionDashboard = firstDetailSection?.detailSections !== undefined && firstDetailSection?.type === 'dashboard';
        }
        if (!hasDetailSectionDashboard) {
          this.use100HeightContainer = true;
        }
      }
    }
  }

  get singlePanel(): LandmarkPanel {
    return this._singlePanel;
  }

  private _setSinglePanel(): void {
    if (this.formSpec.busFormSpec.implementsDashBoardView) {
      this._singlePanel = null;
    } else {
      // only filter out hidden panels. Non-hidden panels that are invalid should
      // be displayed as disabled.
      const panels = this.panels.filter((panel: LandmarkPanel) => !panel.hidden &&  panel.isValid);
      this._singlePanel = panels.length === 1 ? panels[0] : null;
    }
  }

  private _setBackgroundImageUrl(): void {
    if (this.useContextFormBackgroundImage) {

      const relatedImage = !!this.formSpec.busFormSpec.contextForm.background.imageFieldId;
      const imageString = relatedImage ? this.formSpec.busFormSpec.contextForm.background.imageFieldId : this.formSpec.busFormSpec.contextForm.background.image;

      this.contextFormBackgroundImageUrl = this._buildBackgroundImageUrl(relatedImage, imageString);

      if (this.formSpec?.busFormSpec?.contextForm?.background?.foregroundText) {
        this.foregroundDark = this.formSpec.busFormSpec.contextForm.background.foregroundText === 'dark';
        this.foregroundLight = this.formSpec.busFormSpec.contextForm.background.foregroundText === 'light';
      }
    }

    if (this.useFullViewPointBackground) {
      const relatedImage = !!this.formSpec.busFormSpec.background.imageFieldId;
      const imageString = relatedImage ? this.formSpec.busFormSpec.background.imageFieldId : this.formSpec.busFormSpec.background.image;

      this.fullViewPointBackgroundImageUrl = this._buildBackgroundImageUrl(relatedImage, imageString);

      if (this.formSpec?.busFormSpec?.background?.foregroundText) {
        this.foregroundDark = this.formSpec.busFormSpec.background.foregroundText === 'dark';
        this.foregroundLight = this.formSpec.busFormSpec.background.foregroundText === 'light';
      }

      // full page background only works with one panel that is a dashboard panel
      this.fullPageBackgroundNotTwelveColumn = !this.panels[0].useTwelveColumnGrid;
    }
  }

  private _buildBackgroundImageUrl(relatedValueImage: boolean, image: string): SafeStyle {
    if (relatedValueImage) {
      const imageField: LMModelField = this.model.getField(image);
      if (!imageField) {
        return;
      }

      const imageUrl: string = imageField.blobUrl !== undefined ? imageField.blobUrl : imageField.value;
      if (!imageUrl) {
        return;
      }

      const imageUrl2: string = this.lmUrlUtilService.removeParam(imageUrl, 'size');
      return this._sanitizer.bypassSecurityTrustStyle(`url('${imageUrl2}')`);
    } else {
      return this._sanitizer.bypassSecurityTrustStyle(`url('${image}')`);
    }
  }

  get hasTabs(): boolean {
    return this._hasTabs;
  }

  private _calculateHasTabs(): void {
    if (this.formSpec.busFormSpec.implementsDashBoardView) {
      this._hasTabs = false;
    } else {
      const visibleAndValidPanels = this.panels.filter((panel: LandmarkPanel) => !panel.hidden && panel.isValid);

      if (visibleAndValidPanels.length === 0) {
        // no tabs because we don't have any tab that can be used
        this._hasTabs = false;
      } else  {
        // We need to check if we have more than one visible panel
        // the invalid panels are displayed disabled
        const visiblePanels = this.panels.filter((panel: LandmarkPanel) => !panel.hidden);
        this._hasTabs = visiblePanels.length > 1;
      }
    }
  }

  private async activateTab() {
    this.selectedPanelIndex = this.activeTabIndex;
    this.formState.selectedPanelIndex = this.selectedPanelIndex;
    const panel = this.panels[ this.selectedPanelIndex ];

    if (!panel) {
      this.lmLoggerService.log([ this, this.instanceId, 'activateTab()', 'active panel not found, returning' ]);
      return; // safety check!!!!!
    }

    // -----------------------------------------------------------------
    // When clicking a tab replace the url in browser history to
    // update the targetPanel param. This will ensure that if someone bookmarks
    // the page it will have an url hash that will bring them back to
    // that page panel.
    // -----------------------------------------------------------------
    if (!this.implementsCart) {
      const locationHref = this.location.path(true);
      const newPanelName = panel.busFormPanel.panelName;
      const newUrl = this.lmUrlUtilService.updateUrlParams(locationHref, { selectedPanel : newPanelName });
      this.location.replaceState(this.lmUrlUtilService.toRelativeUrl(newUrl));
      this.sendShortcutMessage.emit();
    }

    // const locationHref = this.location.path(true);
    // const parts: string[] = locationHref.split('#');
    // const newPanelName = panel.busFormPanel.panelName;
    // if (parts.length === 1 || (parts.length === 2 && parts[1] !== newPanelName)) {
    //   const newUrl = parts[0] + '#' + newPanelName;
    //   this.location.replaceState(this.lmUrlUtilService.toRelativeUrl(newUrl));
    //   this.sendShortcutMessage.emit();
    // }

    if (this.lazyDashboardConditions && panel.type === 'dashboard') {
      const result = await firstValueFrom(
        this.lmFormPanelBaseService.updateDashboardPanelVisibility(panel.dashBoardPanes, this.model)
      );
      if (result === true) {
        this.panelVisibilityUpdateTimeStamp = this.lmFormPanelBaseService.getPanelDisplayStateLastChanged();
      }
    }

    const compositeComponent: LMFormPanelBase = this.compositeComponents.toArray()[ this.selectedPanelIndex ];

    if (!compositeComponent) {
      return; // safety check!!!!!
    }

    if (panel && panel.type === 'form' && this.useLazyLoadForm && !panel.hidden && !panel.initialized) {
      // render the panel
      panel.initialized = true;
    }

    this.setTabContainerHeight(panel);

    if (!this.isDependentFormPanel(compositeComponent)) {
      return;
    }

    if (this.isFormPanelUrlPanel(compositeComponent)) {
      this.formPanelUrlDisplayed = true;
    }

    // Panel has been touch check if it needs to refresh or reload
    if (compositeComponent.activated) {
      switch (compositeComponent.panelDataState) {
        case 'stale_needsRefresh':
          // fromDataView has not been changed, so make sure the composite component has the most up to date data view
          // when activating
          compositeComponent.fromDataView = this.model.getData();
          compositeComponent.panelDataState = 'fresh';
          compositeComponent.refresh().subscribe();
          break;
        case 'stale_needsReload':
          // this.fromDataView has been changed, so just need to mark as fresh
          compositeComponent.panelDataState = 'fresh'; // fromDataView input flow to the content forcing a reload!!!
          break;
        default:
          if (compositeComponent instanceof LMFormPanelDashboardComponent) {
            // force panels to relayout in case a resize happens after this panel has been activated
            // but the user resized the browser when on another tab.
            (compositeComponent as LMFormPanelDashboardComponent).refreshPanels();
          }
      }
      return;
    } else {
      // added for LMCLIENT-32197. This was a case where on a composite panel, there was a data change from one of the
      // panels. Then there is a second panel, which has not been activated, and has visible when rules on it. Since
      // it hasn't been updated previously, it had a stale fromDataView on it and the visible when rules were incorrect.
      // So we are going to set the fromDataView for the panel here to the up to date dataview
      compositeComponent.fromDataView = this.model.getData();
    }

    // --------------------------------------------------------------------------------------
    // Include the busy indicator for form panels too as it takes
    // time to render a form panel.
    // --------------------------------------------------------------------------------------
    this.showBusyIndicator(this.sohoBusyIndicator);

    setTimeout(() => {
      if (this.componentDestroyed) {
        this.lmLoggerService.log([ this, this.instanceId, 'activateTab() > timeout > component was destroyed before expected, returning.' ]);
        // safety in case the user has initiated a list but quickly navigated
        // to another screen and ngOnDestroy is in a race condition with this
        // timeout.
        return;
      }

      this.activateFormPanel(compositeComponent);
      this.hideBusyIndicator(this.sohoBusyIndicator);
    });

  }

  private activateFormPanel(formPanel: LMFormPanelBase) {
    formPanel.panelDataState = 'fresh';
    formPanel.activated = true;  // this should trigger the template to run
  }

  onFormPanelContentLoaded(event: any) {
    // Need to set this in a timeout, else we get an expression changed error on load
    this.ngZone.runOutsideAngular(() => setTimeout(() => this.handlePanelHeightChange()));
    this.hideBusyIndicator(this.sohoBusyIndicator);
  }

  private get activeTabIndex() {
    const index = (this.sohoTabsComponent?.getActiveTab()?.length > 0)
      ? this.sohoTabsComponent.getActiveTab()[ 0 ].dataset.index
      : -1;
    return Number(index);
  }

  private updateFromDataView(sourceIsPanel?: boolean, originatingList?: string, originatingPanel?: string): void {
    this.lmLoggerService.log([ this, this.instanceId, 'updateFromDataView()', 'start' ]);
    const newFromDataView = this.model.getData();
    const fromDataViewChanged = this.hasFromDataViewChanged(newFromDataView);

    if (!this.formSpec.busFormSpec.implementsDashBoardView) {
      this.markTabsForReload(fromDataViewChanged, sourceIsPanel, originatingList, originatingPanel);
    } else if (this.dashboardViewComponent && !fromDataViewChanged) {
      const  refreshRequest: LMRefreshRequest = { originatingSourceId: originatingList };
      this.dashboardViewComponent.refresh(refreshRequest).subscribe();
    }

    if (fromDataViewChanged) {
      this.fromDataView = newFromDataView;
    }

    this.lmLoggerService.log([ this, this.instanceId, 'updateFromDataView()', 'end' ]);
  }

  private hasFromDataViewChanged(newFromDataView: LMDataView): boolean {
    const resourceId = this.fromDataView ? this.fromDataView.resourceId : '';
    const newResourceId = newFromDataView ? newFromDataView.resourceId : '';

    return resourceId !== newResourceId;
  }

  private isDependentFormPanel(formPanelBase: LMFormPanelBase) {
    if (!formPanelBase) {
      return false;
    }

    if (formPanelBase instanceof LMFormPanelFormComponent && !formPanelBase.formSpec.dependentForm) {
      return false;
    }

    return true;
  }

  private isFormPanelUrlPanel(formPanelBase: LMFormPanelBase) {
    if (!formPanelBase) {
      return false;
    }

    return formPanelBase instanceof LMFormPanelUrlComponent;
  }

  private markTabsForReload(fromDataViewChgd: boolean, sourceIsPanel?: boolean, originatingList?: string, originatingPanel?: string) {
    const selectedIndex = this.sohoTabsComponent ? this.activeTabIndex : 0;
    const components  = this.compositeComponents.toArray();
    components.forEach((compositeComponent, i) => {
      if (this.isDependentFormPanel(compositeComponent)) {
        if (i === selectedIndex ) {
          this.updateCurrentPanelData(compositeComponent, fromDataViewChgd, sourceIsPanel, originatingList, originatingPanel);
        } else {
          if (compositeComponent.activated && compositeComponent.panelDataState !== 'stale_needsReload') {
            compositeComponent.panelDataState = fromDataViewChgd ? 'stale_needsReload' : 'stale_needsRefresh';
          }
        }
      }
    });
  }

  private updateCurrentPanelData(compositeComponent: LMFormPanelBase, fromDataViewChgd: boolean, sourceIsPanel?: boolean, originatingList?: string, originatingPanel?: string) {
    if (compositeComponent.activated) {
      this.refreshActivePanel(compositeComponent, fromDataViewChgd, sourceIsPanel, originatingList, originatingPanel);
    } else {
      this.activateFormPanel(compositeComponent);
    }
  }

  private refreshActivePanel(compositeComponent: LMFormPanelBase, fromDataViewChgd: boolean, sourceIsPanel: boolean, originatingList?: string, originatingPanel?: string) {
    // compositeComponent.needsReload = false;
    compositeComponent.panelDataState = 'fresh';
    const isFormPanelUrl = compositeComponent instanceof LMFormPanelUrlComponent;

    // url form panels are excluded here since they sould be updated if the panel is showing.
    if (fromDataViewChgd && !isFormPanelUrl) {
      // the user changed the dataView from paging operation so
      // the new fromDataView should force the panels to refresh!!!
      return;
    }

    // we need to sort out the message passed to the hand coded screens and refreshing the form.
    // if data was updated but keys did not change should be relayed to handcoded apps but also
    // if keys have change.
    // This is used to send a message to the hand coded apps that the panel changed. It was put in primarally for action forms.
    if (isFormPanelUrl) {
      const lmFormPanelUrlComponent: LMFormPanelUrlComponent = compositeComponent as LMFormPanelUrlComponent;
      // form already displayed confirmation message soneed to send one along.
      lmFormPanelUrlComponent.fireFormActionComplete(LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED,
        { message: '', resourceId: this.model.getData().resourceId });
      compositeComponent.refresh().subscribe();
    }
    // user ran an action on a panel!!!
    if (sourceIsPanel) {
      this.refreshFromPanelTransaction(compositeComponent, originatingList, originatingPanel);
    } else {
      compositeComponent.refresh().subscribe();
    }
  }

  private refreshFromPanelTransaction(compositeComponent: LMFormPanelBase, originatingList?: string, originatingPanel?: string) {
    // TODO: let move to this code.  The formpanel can stop the refresh!
    // const refreshRequest: LMRefreshRequest = {originatingSourceId: originatingList, originatingPanelSourceId: originatingPanel};
    // compositeComponent.refresh(refreshRequest).subscribe(); // DO NOT FORCE REFRESH
    //
    // if (compositeComponent instanceof LMFormPanelDashboardComponent) {
    //   // force panels to relayout in case a resize happens after this panel has been activated
    //   // but the user resized the browser when on another tab.
    //   (compositeComponent as LMFormPanelDashboardComponent).refreshPanels();  // why would this need to be done here.
    // }

    // if a list or other panel triggered the event they
    // should have refreshed themselves!!!
    const activePanelChanged = compositeComponent.panelId !== originatingPanel;
    const refreshRequest: LMRefreshRequest = { originatingSourceId: originatingList };
    if (activePanelChanged) {
      compositeComponent.refresh().subscribe(); // DO NOT FORCE REFRESH
    } else {
      if (compositeComponent instanceof LMFormPanelListComponent
        || compositeComponent instanceof LMFormPanelMultiListComponent
        || compositeComponent instanceof LMFormPanelDashboardComponent) {
        // need to call list refresh because the list transaction may have begun in
        // a list detail section, in that case we need to refresh the panel list
        // need to call refresh on mulit list and dashboard to refresh the other widgets in the panel
        compositeComponent.refresh(refreshRequest).subscribe();
      }

      if (compositeComponent instanceof LMFormPanelDashboardComponent) {
        // force panels to relayout in case a resize happens after this panel has been activated
        // but the user resized the browser when on another tab.
        (compositeComponent as LMFormPanelDashboardComponent).refreshPanels();  // why would this need to be done here.
      }
      // if a list or other panel triggered the event they
      // should have refreshed themselves!!!
    }
  }

  private selectSohoTab(tabId: string) {
    if (this.sohoTabsComponent) {
      this.sohoTabsComponent.select('#' + tabId, true);
    }
  }

  findNearestVisibleValidPanel(selectedIndex: number): LandmarkPanel {

    for (let i = selectedIndex; i > -1; i--) {
      const panel = this.panels[i];
      if (!panel.hidden && panel.isValid) {
        return panel;
      }
    }

    for (let i = selectedIndex, length = this.panels.length; i < length; i++) {
      const panel = this.panels[i];
      if (!panel.hidden && panel.isValid) {
        return panel;
      }
    }

    return null;
  }

  isModified(): boolean {
    // composite form ( context form and form panels)
    if (this.model.isDirty) {
      return true;
    }

    return this.isRelatedModified();
  }

  isRelatedModified(): boolean {

    if (this.dashboardViewComponent) {
      return this.dashboardViewComponent.isModified();
    } else {
      const formPanels: LMFormPanelBase[] = this.compositeComponents.toArray();
      for (let i = 0, len = formPanels.length; i < len; i++) {
        const formPanel = formPanels[ i ];
        if (!(formPanel instanceof LMFormPanelFormComponent) && formPanel.isDirty()) {
          return true;
        }
      }
    }

    return false;
  }

  // called from lm-form.component handleTransactionStatus() when setting an error on a form field; if form panel is
  // set for lazy loading, need to indicate to initialize (render) the form panel before setting the error message on the field
  canActivatePanel(fieldId): boolean {
    if (!this.useLazyLoadForm) {
      return false;
    }

    const panelIds: Array<string> = this.lmFormPanelBaseService.getPanelIdsForFieldIdComposite(fieldId);

    // iterate the panelIds array to find the first panel that meets the criteria
    for (let index = 0, len = panelIds.length; index < len; index++) {
      const panelId = panelIds[ index ];
      const aryPanels = this.panels.filter( (panel) => (panel.id === panelId && !panel.hidden && panel.type === 'form' && !panel.initialized) );

      if (aryPanels.length > 0) {
        // render the panel
        aryPanels[0].initialized = true;
        return true;
      }
    }

    return false;
  }

  runRefresh() {
    // do nothing -- listening to model events!!!
  }

  runSaveAction(): Observable<boolean> {
    if (this.dashboardViewComponent && this.dashboardViewComponent.isModified()) {
      return this.dashboardViewComponent.runSaveAction();
    }

    const formPanels: LMFormPanelBase[] = this.compositeComponents.toArray();
    // todo dpb selectedFormPanel isn't used. Please review.
    const selectedFormPanel: LMFormPanelBase = formPanels[ this.selectedPanelIndex ];
    const selectedFormPanelId = selectedFormPanel ? selectedFormPanel.panelId : '';

    return new Observable<boolean>(observer => {
      this.runSavePanels(formPanels).subscribe(
        (continueOn: boolean) => {
          formPanels.forEach(savedPanel => {
            // if the form changes, it will send an event to tell the current panel
            // to update.  All other activated tabs should be mark needs reload or refresh
            if (selectedFormPanelId === savedPanel.panelId && savedPanel.activated && savedPanel.panelDataState !== 'stale_needsReload') {
              savedPanel.panelDataState = 'stale_needsRefresh';
            }
          });
          // this.markTabsForReload(false, false);
          observer.next(continueOn);
          observer.complete();
        },
        (err => {
          const errorPanel: LMFormPanelBase = err;
          if (errorPanel) {
            if (this.hasTabs && this.sohoTabsComponent) {
              this.sohoTabsComponent.select('#' + errorPanel.panelId);
            }
            observer.next(false);
            observer.complete();
          }
        }));

    });
  }

  private updateVisibleWhenTabs() {
    this.lmFormPanelBaseService.updateCompositePanels(this.formSpec.busFormSpec, this.model, false, true, this.isCreateReportMode);

    if (this.formSpec.busFormSpec.implementsDashBoardView) {
      this.panelVisibilityUpdateTimeStamp = this.lmFormPanelBaseService.getPanelDisplayStateLastChanged();

      if (this.dashboardViewComponent) {
        setTimeout(() => this.dashboardViewComponent.refreshHomepage(true));
      }

      return;
    }

    const isShowingSinglePanel = !this.hasTabs && this.singlePanel;
    this._calculateHasTabs();

    if (isShowingSinglePanel && !this.hasTabs) {
      const newSinglePanel = this.findNearestVisibleValidPanel(this.selectedPanelIndex);
      const newSinglePanelId = newSinglePanel ? newSinglePanel.id : '';
      if (this.singlePanel.id !== newSinglePanelId) {
        this.singlePanel.selected = false;
        this._hasPendingSelection = true;
        this._pendingSelectedPanel = newSinglePanel;
        this._singlePanel = null;   // this is need to clear out the single panel angular template
      }
      return;
    }

    this._setSinglePanel();
    // case multi panels showing (tabs)
    const selectedPanel = this.panels[ this.selectedPanelIndex ];
    if (selectedPanel.hidden || !selectedPanel.isValid) {
      selectedPanel.selected = false;
      const nearestPanel = this.findNearestVisibleValidPanel(this.selectedPanelIndex);

      if (nearestPanel) {
        nearestPanel.selected = true;
        if (this.sohoTabsComponent) {
          setTimeout(() => this.selectSohoTab(nearestPanel.id), 1);
          this.runHandleResizeTabsComponent = true;
        }
      }
    }
  }

  private handlePendingSelection() {
    this._setSinglePanel();

    if (this._pendingSelectedPanel) {
      this._pendingSelectedPanel.selected = true;
      const pendingSelectionId = this._pendingSelectedPanel.id;
      this._pendingSelectedPanel = null;

      if (this.hasTabs) {
        setTimeout(() => this.selectSohoTab(pendingSelectionId), 1);
      }
    }
  }

  saveComponentState(): void {
    this.compositeComponents.toArray()
      .forEach(compositeComponent =>
        compositeComponent.saveCompositeComponentState());
  }

  clearComponentState(): void {
    this.compositeComponents.toArray()
      .forEach(compositeComponent =>
        compositeComponent.clearCompositeComponentState());
  }

  trackById(index: number, panel: LandmarkPanel): string {
    return panel.id;
  }

  private runSavePanels(formPanels: LMFormPanelBase[]): Observable<boolean> {

    this.showBusyIndicator();
    const saveWorker: LMPanelSaveWorker = new LMPanelSaveWorker(formPanels);
    return saveWorker.start();
  }

  public get componentDestroyed(): boolean {
    return this.m_componentDestroyed;
  }

  protected setComponentDestroyed() {
    this.m_componentDestroyed = true;
  }

  private buildContextFormClasses(): void {
    if (this.contextForm) {
      this.contextFormClasses = 'panel-border composite-form-context-form';

      if (!this.useFullViewPointBackground) {
        if (this.personalize)
          this.contextFormClasses += ' is-personalizable';
        else
          this.contextFormClasses += ' lm-secondary-background';
      }

      if (this.useContextFormBackgroundImage)
        this.contextFormClasses += ' use-background-image background-image-attributes';

      if (this.showContextFormOnSide) {
        this.contextFormClasses += ' context-form-side';

        if (this.lmContextService.inMingle())
          this.contextFormClasses += ' in-mingle';
      }

      if (this.foregroundLight)
        this.contextFormClasses += ' foreground-light';

      if (this.foregroundDark)
        this.contextFormClasses += ' foreground-dark';
    }
  }

  alternateTabs() {
    // no colors in flyout
    if (this.isInOverlay)
      return false;
    // only use alternate tabs in light context form when in default vibrant light
    const lightContextAlternate = this.lightContextForm &&
        ((this.lmThemeService.getCurrentThemeId() === 'theme-new-light' && this.lmThemeService.getCurrentColorId() !== LMColorId.alabaster));
    this.useAlternateTabs = (!this.verticalTabs && !this.useThemeColor && !(lightContextAlternate) || this.parentIsModal);
  }

  isPersonalizable() {
    // no colors in flyout
    if (this.isInOverlay)
      return false;
    // only use is-personalizable in light context form when in default vibrant light
    const lightContextPersonalizable = this.lightContextForm &&
        ((this.lmThemeService.getCurrentThemeId() === 'theme-new-light' && this.lmThemeService.getCurrentColorId() !== LMColorId.alabaster));
    this.usePersonalizableTabs = !this.verticalTabs && this.useThemeColor && !this.parentIsModal && !(lightContextPersonalizable);
  }

  onVisibleWhenEvent(elementRef: ElementRef) {
    if ((elementRef?.nativeElement?.parentElement as HTMLElement)?.closest('.lm-composite-content-area')) {
      this.doRecalculateHeight = true;
    }
  }

  handleBlockingErrorField(event: LMInvalidFieldEvent) {
    if (event.panelId) {
      // find the visible tab and select it
      if (this.panels.some( (panel: LandmarkPanel) => panel.id === event.panelId && !panel.hidden)) {
        this.sohoTabsComponent.select('#' + event.panelId);

      } else {
        // cannot find a panel, so display the error in a dialog
        this.lmErrorUtilService.handleUnexpectedError({ title: Soho.Locale.translate('Error'), message: event.alert.errorMessage });
      }
    }

    event.alert.scrollIntoView(false);
  }

  moveContextForm(onSide: boolean) {
    this.showContextFormOnSide = onSide;
    this.buildContextFormClasses();
    this.handlePanelHeightChange();
  }

  private showBusyIndicator(showBusyIndicator?: SohoBusyIndicatorDirective): void {
    if (this.lmContextService.useListFormOverlay()) {
      if (this.isInOverlay) {
        // bubble up to the overlay to show the busy indicator in the overlay
        this.lmOverlayService.busyIndicator(true);
      } else {
        this.lmBusyIndicatorService.showBusyIndicator(showBusyIndicator);
      }
    } else {
      this.lmBusyIndicatorService.showBusyIndicator(showBusyIndicator);
    }
  }

  private hideBusyIndicator(showBusyIndicator?: SohoBusyIndicatorDirective): void {
    if (this.lmContextService.useListFormOverlay()) {
      if (this.isInOverlay) {
        // bubble up to the overlay to hide the busy indicator in the overlay
        this.lmOverlayService.busyIndicator(false);
      } else {
        this.lmBusyIndicatorService.hideBusyIndicator(showBusyIndicator);
      }
    } else {
      this.lmBusyIndicatorService.hideBusyIndicator(showBusyIndicator);
    }
  }
}
